import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Check, Users, GraduationCap, Building2, Handshake, Sparkles, Loader2 } from "lucide-react";

const PLAN_ICON = { parent: Users, teacher: GraduationCap, school: Building2, district: Building2, reseller: Handshake };

export function PricingPlans({ compact = false }) {
  const { user, role, loading } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState(null);
  const [annual, setAnnual] = useState(false);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    api.getPlans().then((d) => setPlans(d.plans)).catch(() => setPlans([]));
  }, []);

  const activePlan = user?.subscription?.status === "active" ? user.subscription.plan : null;

  const priceFor = (p) => {
    const want = annual ? "year" : "month";
    return p.prices.find((x) => x.interval === want) || p.prices[0];
  };

  const subscribe = async (p) => {
    const price = priceFor(p);
    if (loading) return;
    if (!user) { navigate("/signup"); return; }
    if (role === "student") { toast.error("Ask a parent or teacher to upgrade your account."); return; }
    setBusy(price.lookup_key);
    try {
      const { checkout_url } = await api.checkout({ lookup_key: price.lookup_key, origin_url: window.location.origin });
      window.location.href = checkout_url;
    } catch (e) {
      toast.error(formatApiError(e));
      setBusy(null);
    }
  };

  if (!plans) return <div className="py-12 text-center"><Loader2 className="mx-auto animate-spin text-[#0D9488]" /></div>;

  return (
    <div>
      {/* Billing toggle */}
      <div className="mb-8 flex items-center justify-center gap-3">
        <span className={`font-body text-sm font-semibold ${!annual ? "text-[#1F2937]" : "text-[#94A3B8]"}`}>Monthly</span>
        <button
          onClick={() => setAnnual((a) => !a)}
          data-testid="pricing-billing-toggle"
          className={`relative h-8 w-14 rounded-full transition-colors ${annual ? "bg-[#10B981]" : "bg-[#CBD5E1]"}`}>
          <span className={`absolute top-1 left-0 h-6 w-6 rounded-full bg-white shadow transition-transform ${annual ? "translate-x-7" : "translate-x-1"}`} />
        </button>
        <span className={`font-body text-sm font-semibold ${annual ? "text-[#1F2937]" : "text-[#94A3B8]"}`}>Annual</span>
        <span className="rounded-full bg-[#F59E0B]/15 px-2.5 py-1 text-xs font-bold text-[#B45309]">Save 20%</span>
      </div>

      <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
        {/* Free tier */}
        <div className="flex flex-col rounded-3xl border border-[#E5DECF] bg-white p-6" data-testid="plan-card-free">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#F2ECE1] text-[#6B7280]"><Sparkles size={22} /></div>
          <h3 className="mt-3 font-heading text-xl font-bold text-[#1F2937]">Free</h3>
          <p className="font-body text-sm text-[#6B7280]">Try a sample lesson in every subject</p>
          <div className="mt-3 font-heading text-3xl font-extrabold text-[#1F2937]">$0</div>
          <ul className="mt-4 flex-1 space-y-2 text-sm text-[#1F2937]">
            {["One free lesson per subject", "Read-aloud audio", "Points & rewards", "No card required"].map((f) => (
              <li key={f} className="flex items-start gap-2"><Check size={16} className="mt-0.5 shrink-0 text-[#10B981]" /> {f}</li>
            ))}
          </ul>
          <button onClick={() => navigate(user ? "/" : "/signup")} data-testid="plan-cta-free"
            className="mt-5 rounded-full border-2 border-[#0D9488] px-4 py-2.5 font-heading font-bold text-[#0D9488] transition-colors hover:bg-[#0D9488]/5">
            {user ? "Start Learning" : "Start Free"}
          </button>
        </div>

        {plans.map((p) => {
          const Icon = PLAN_ICON[p.plan] || Users;
          const price = priceFor(p);
          return (
            <motion.div
              key={p.plan}
              whileHover={{ y: -4 }}
              data-testid={`plan-card-${p.plan}`}
              className={`relative flex flex-col rounded-3xl border-2 bg-white p-6 ${p.popular ? "border-[#0D9488] shadow-[0_8px_30px_rgb(59,66,159,0.12)]" : "border-[#E5DECF]"}`}>
              {p.popular && <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-[#0D9488] px-3 py-1 text-xs font-bold text-white">Most Popular</span>}
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#0D9488]/10 text-[#0D9488]"><Icon size={22} /></div>
              <h3 className="mt-3 font-heading text-xl font-bold text-[#1F2937]">{p.name}</h3>
              <p className="font-body text-sm text-[#6B7280]">{p.tagline}</p>
              <div className="mt-3 flex items-end gap-1">
                <span className="font-heading text-3xl font-extrabold text-[#1F2937]">{price.label.split("/")[0]}</span>
                <span className="pb-1 font-body text-sm text-[#6B7280]">/{price.interval === "year" ? "yr" : "mo"}</span>
              </div>
              <ul className="mt-4 flex-1 space-y-2 text-sm text-[#1F2937]">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2"><Check size={16} className="mt-0.5 shrink-0 text-[#10B981]" /> {f}</li>
                ))}
              </ul>
              <button
                onClick={() => subscribe(p)}
                disabled={busy === price.lookup_key || loading || activePlan === p.plan}
                data-testid={`plan-cta-${p.plan}`}
                className={`mt-5 flex items-center justify-center gap-2 rounded-full px-4 py-2.5 font-heading font-bold transition-transform hover:scale-[1.02] active:scale-95 ${activePlan === p.plan ? "bg-[#10B981]/15 text-[#047857] cursor-default" : "text-white " + (p.popular ? "bg-[#0D9488] hover:bg-[#0B7F74]" : "bg-[#1F2937] hover:bg-black")} ${loading ? "opacity-60" : ""}`}>
                {activePlan === p.plan ? <><Check size={18} /> Current Plan</> : busy === price.lookup_key ? <Loader2 size={18} className="animate-spin" /> : "Subscribe"}
              </button>
            </motion.div>
          );
        })}
      </div>
      {!compact && <p className="mt-6 text-center font-body text-xs text-[#94A3B8]">Test mode — use card 4242 4242 4242 4242, any future expiry, any CVC. No real charges.</p>}
    </div>
  );
}
